/**
 * This file contains utility functions for customizing the GUI fields and groups for the Race* components.
 */
import type { SelectLine, SelectLineValueType } from '@/objects';
import type { Race } from '@/objects/generated';
import type { FieldDef } from '@/objects/gui';
import { useCommonStoreRef } from '@/stores';
import { propertyof } from '@/utilities';

/**
 * Creates a filter function for the LevelID source to only include null value when null is already set and it is not new
 * @returns A function that filters the LevelID source for a given race.
 */
const makeLevelIDFilter = () => {
    const common = useCommonStoreRef();

    const LevelIDSourceFilter = (race: Race) => {
        const inactive = common.SportLevels.value.filter((x) => x.Inactive).map((x) => x.ID);
        const skipifnotcurrent: Array<SelectLineValueType> = [null, ...inactive];
        const valuesToInclude = Array.from(common.SportLevelsForEventWithUseEventAsSelectLine.value.values()).filter(
            (x) =>
                !skipifnotcurrent.includes(x.value) || // include the non null and active
                race.SportsLevelID == x.value, // include the value if it is already set
        );

        // update if field does not have the correct value
        if (!valuesToInclude.map((x) => x.value).includes(race.SportsLevelID)) {
            if (valuesToInclude.length > 0) {
                race.SportsLevelID = valuesToInclude[0].value as number | null;
            } else {
                console.warn('race.SportsLevelID not set as we have no data in source');
            }
        }

        return valuesToInclude;
    };
    return LevelIDSourceFilter;
};

/**
 * Modifies a field to use the LevelID source filter.
 * @param field The field to modify.
 * @param LevelIDSourceFilter The LevelID source filter function.
 * @returns The modified field.
 */
const modifySportsLevelIdField = (field: FieldDef<Race, any>, LevelIDSourceFilter: (race: Race) => SelectLine[]) => {
    if (field.Name === propertyof<Race>('SportsLevelID')) {
        field.DetailEditorProps = { datasource: LevelIDSourceFilter };
        field.CellEditorProps = { datasource: LevelIDSourceFilter };
    }
    return field;
};

const modifyCancellationDeadlineField = (fields: FieldDef<Race>[]) => {
    const cancellationDeadlineField = fields.find((field) => field.Name === propertyof<Race>('CancellationDeadline'));

    if (!cancellationDeadlineField) {
        return;
    }

    // Disable the DateTimeInput in the detail dialog if we have already set the cancellation deadline
    cancellationDeadlineField.DetailEditorPropsGetter = (row) => {
        return {
            disabled: row.CancellationDeadline != null,
            ...cancellationDeadlineField.DetailEditorProps,
        };
    };

    // Make the grid cell non-editable if we have already set the cancellation deadline
    cancellationDeadlineField.Editable = (row) => row.CancellationDeadline == null;
};

/**
 * Modifies the groups of fields to use the LevelID source filter.
 * @param groups The groups of fields to modify.
 * @returns The modified groups of fields.
 */
export const customGuiGroups = (groups: FieldDef<Race>[][]) => {
    const LevelIDSourceFilter = makeLevelIDFilter();

    modifyCancellationDeadlineField(groups.flatMap((group) => group));

    return groups.map((fields) => fields.map((field) => modifySportsLevelIdField(field, LevelIDSourceFilter)));
};

/**
 * Customizes the GUI fields for the RaceView.
 * @param fields The fields to modify.
 * @returns The modified fields.
 */
export const customGuiFields = (fields: FieldDef<Race>[]) => {
    const LevelIDSourceFilter = makeLevelIDFilter();

    modifyCancellationDeadlineField(fields);

    return fields.map((field) => modifySportsLevelIdField(field, LevelIDSourceFilter));
};
